import type { CSSProperties, FC, ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { Control, FieldErrors, FieldArrayWithId } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Button, Flex, Form, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { UpdateFormValues } from '@modules/ai-agent/lib/types';
import { Text } from '@shared/ui/antd';

import { DeleteOutlinedSmall, DragMenu, PlusBackground } from '@shared/assets/icons/components';

import styles from '../../prompt.module.scss';

interface StageRowProps {
  id: string;
  children: (args: {
    attributes: any;
    listeners: any;
    setActivatorNodeRef: (element: HTMLElement | null) => void;
    isDragging: boolean;
  }) => ReactNode;
}

const StageRow: FC<StageRowProps> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: transform ? CSS.Translate.toString(transform) : undefined,
    transition: isDragging ? undefined : transition,
    width: '100%',
    minWidth: 0,
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners, setActivatorNodeRef, isDragging })}
    </div>
  );
};

interface DialogStepsProps {
  stageFields: FieldArrayWithId<UpdateFormValues, 'stages', 'id'>[];
  control: Control<UpdateFormValues>;
  errors: FieldErrors<UpdateFormValues>;
  isLoading: boolean;
  dialogStepsPlaceholders: string[];
  stageFieldOptions: { value: number; label: string }[];
  onAddStage: () => void;
  onRemoveStage: (index: number) => void;
  onMoveStage: (fromIndex: number, toIndex: number) => void;
  t: TFunction<'translation', 'prompt'>;
}

export const DialogSteps: FC<DialogStepsProps> = ({
  stageFields,
  control,
  errors,
  isLoading,
  dialogStepsPlaceholders,
  stageFieldOptions,
  onAddStage,
  onRemoveStage,
  onMoveStage,
  t,
}) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  return (
    <Flex vertical className={styles.element} gap={16}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('dialogSteps')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('dialogStepsTooltip')}
        </Text>
      </Flex>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;

          const fromIndex = stageFields.findIndex((item) => item.id === active.id);
          const toIndex = stageFields.findIndex((item) => item.id === over.id);

          if (fromIndex === -1 || toIndex === -1) return;

          onMoveStage(fromIndex, toIndex);
        }}
      >
        <SortableContext
          items={stageFields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          <Flex vertical gap={12} className={styles.element_inside}>
            <Flex vertical gap={12} style={{ width: '100%' }}>
              {stageFields.map((field, index) => (
                <StageRow key={field.id} id={field.id}>
                  {({ attributes, listeners, setActivatorNodeRef, isDragging }) => (
                    <Flex
                      gap={12}
                      className={styles.stageRow}
                      style={{ opacity: isDragging ? 0.9 : 1 }}
                    >
                      <div className={styles.stageRowContent}>
                        <div className={styles.stagePrimaryCol}>
                          <Text variant="sm" fontWeight={400} className={styles.stageLabel}>
                            {index + 1}. {t('stageLabel')}
                          </Text>

                          <Form.Item
                            help={errors.stages?.[index]?.prompt?.message}
                            validateStatus={errors.stages?.[index]?.prompt ? 'error' : ''}
                            className={styles.label}
                          >
                            <Controller
                              control={control}
                              name={`stages.${index}.prompt`}
                              render={({ field }) => (
                                <TextArea
                                  {...field}
                                  id={`stages-${index}`}
                                  placeholder={
                                    dialogStepsPlaceholders[index] ?? dialogStepsPlaceholders.at(-1)
                                  }
                                  className={styles.stageArea}
                                  style={{ resize: 'none' }}
                                  autoSize={
                                    isDragging ? { minRows: 1, maxRows: 1 } : { minRows: 2 }
                                  }
                                  disabled={isLoading}
                                />
                              )}
                            />
                          </Form.Item>
                        </div>

                        <div className={styles.stageSecondaryCol}>
                          <Text variant="sm" fontWeight={500} className={styles.stageFieldLabel}>
                            {t('stageFieldLabel')}
                          </Text>

                          <Form.Item
                            help={errors.stages?.[index]?.agent_field_id?.message}
                            validateStatus={errors.stages?.[index]?.agent_field_id ? 'error' : ''}
                            className={styles.stageFieldItem}
                          >
                            <Controller
                              control={control}
                              name={`stages.${index}.agent_field_id`}
                              render={({ field }) => {
                                const normalizedValue = Number(field.value);

                                return (
                                  <Select
                                    value={
                                      Number.isFinite(normalizedValue) && normalizedValue > 0
                                        ? normalizedValue
                                        : undefined
                                    }
                                    onChange={(val) => field.onChange(Number(val))}
                                    options={stageFieldOptions}
                                    className={styles.stageFieldSelect}
                                    disabled={isLoading}
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder={t('selectField')}
                                  />
                                );
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>

                      <Flex gap={8} className={styles.stageActions}>
                        <button
                          type="button"
                          className={styles.stageActionButtonDrag}
                          ref={setActivatorNodeRef}
                          {...attributes}
                          {...listeners}
                          aria-label="Drag stage"
                        >
                          <DragMenu />
                        </button>

                        <button
                          type="button"
                          className={styles.stageActionButton}
                          onClick={() => onRemoveStage(index)}
                          aria-label="Remove stage"
                        >
                          <DeleteOutlinedSmall color="#A7A8AE" />
                        </button>
                      </Flex>
                    </Flex>
                  )}
                </StageRow>
              ))}
            </Flex>

            <Button
              icon={<PlusBackground fill="var(--color-text-secondary)" />}
              iconPosition="end"
              onClick={onAddStage}
              type="text"
              className={styles.button_additional}
            >
              {t('addDialogStep')}
            </Button>
          </Flex>
        </SortableContext>
      </DndContext>
    </Flex>
  );
};
